package com.bikompanion.data.gbfs

import com.bikompanion.domain.BoundingBox
import com.bikompanion.domain.COVERAGE_RADIUS_DEGREES
import com.bikompanion.domain.CoveragePoint
import com.bikompanion.domain.NetworkCoverage
import kotlin.math.floor

/**
 * How close two docks have to be before only one of them is kept, in degrees.
 *
 * About 220 m. Central Barcelona has docks a block apart and drawing a circle around each is
 * work the map can't see, so stations are snapped to a grid this size and the first one in
 * each cell speaks for the rest. Deliberately below the walking radius, and below it by
 * enough that the diagonal of a cell still fits: whoever gets dropped is inside the circle of
 * whoever was kept, so thinning never opens a hole.
 */
private const val THINNING_DEGREES = 0.002

/**
 * The ground a set of stations covers, as the reach of each dock.
 *
 * Circles around docks rather than one rectangle because the rectangle lies: AMBici's docks
 * ring Barcelona across a dozen municipalities, and the box around them takes in a great deal
 * of hillside that has never had a bike in it.
 *
 * Circles rather than the grid cells this used to snap to, because a grid draws the edge of
 * the service area in squares that are nothing to do with where the docks are — a station
 * near a cell boundary ends up with a dark square against it, which reads as a place the
 * bikes stop rather than as the artefact of the grid that it is. A radius around the dock
 * itself has no edges to line up with.
 *
 * Returns null for an empty list: a network with no stations covers nothing, which is not the
 * same as covering everywhere.
 */
internal fun coverageOf(stations: List<GbfsStationInformationDto>): NetworkCoverage? {
    if (stations.isEmpty()) return null

    val occupied = mutableSetOf<Long>()
    val points = mutableListOf<CoveragePoint>()
    stations.forEach { station ->
        val row = floor(station.lat / THINNING_DEGREES).toInt()
        val column = floor(station.lon / THINNING_DEGREES).toInt()
        // Packed rather than a Pair so the set holds primitives and the whole pass stays
        // allocation-free for the stations it drops, which is most of them downtown.
        if (occupied.add(row.toLong() shl 32 or (column.toLong() and 0xFFFFFFFFL))) {
            points += CoveragePoint(latitude = station.lat, longitude = station.lon)
        }
    }

    return NetworkCoverage(
        // Grown by the radius so the box still contains every circle drawn inside it, and the
        // cheap "is this network on screen" test can't reject a network whose outermost dock
        // reaches into the viewport.
        bounds = BoundingBox(
            south = stations.minOf { it.lat } - COVERAGE_RADIUS_DEGREES,
            west = stations.minOf { it.lon } - COVERAGE_RADIUS_DEGREES,
            north = stations.maxOf { it.lat } + COVERAGE_RADIUS_DEGREES,
            east = stations.maxOf { it.lon } + COVERAGE_RADIUS_DEGREES,
        ),
        points = points,
    )
}
